"use client";

import { useId, useMemo } from "react";
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer } from "recharts";
import { StockLogo } from "@/components/stock-logo";
import { EmptyState } from "@/components/empty-state";
import type { DividendLineItem, MonthlyActualVsProjected } from "@/lib/dividend-tracker/aggregation";
import { computeNiceYAxisTicks } from "@/lib/price-chart-utils";
import { formatCurrency, formatCompactCurrency } from "@/lib/format";

const GREEN = "#3F7A5B";

/** Exportiert (Feedback-Runde 13 Punkt 3, 2026-07-22): dividend-history-forecast-chart.tsx
 * nutzt dieselbe Zeilenliste für seinen neuen Pro-Symbol-Jahres-Tooltip, statt sie zu
 * duplizieren. */
export function ItemList({
  items,
  currency,
  netLabel = false,
}: {
  items: DividendLineItem[];
  currency: string;
  /** Kennzeichnet jeden Betrag zusaetzlich klein mit "(netto)" (backlog.md -> [Punkt 12])
   * - nur fuer noch ausstehende/prognostizierte Betraege gedacht (dort lohnt der Hinweis,
   * da es eine Schaetzung ist), nicht fuer bereits erhaltene. Als eigenes kleines Suffix
   * NACH dem Betrag statt eigener Spalte, damit die Betraege selbst innerhalb einer Liste
   * weiterhin sauber untereinander ausgerichtet bleiben. */
  netLabel?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1">
      {items.map((item, i) => (
        <li key={`${item.symbol}-${i}`} className="flex items-center justify-between gap-3 text-xs">
          <span className="flex min-w-0 items-center gap-1.5 text-ink-secondary">
            <StockLogo symbol={item.symbol} logoUrl={item.logoUrl} size={20} />
            <span className="truncate">{item.name}</span>
            {item.confirmed === false && <span className="shrink-0 text-muted-foreground">(ca.)</span>}
          </span>
          <span className="shrink-0 font-tabular text-ink">
            {formatCurrency(item.amountNet, currency)}
            {netLabel && <span className="font-sans text-muted-foreground"> (netto)</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function MonthTooltip({
  active,
  payload,
  currency,
}: {
  active?: boolean;
  payload?: { payload: MonthlyActualVsProjected }[];
  currency: string;
}) {
  if (!active || !payload?.[0]) return null;
  const point = payload[0].payload;
  return (
    <div
      className="max-w-64 rounded-md border border-hairline bg-card p-3 shadow-lg"
      style={{ boxShadow: "0 4px 16px rgba(22,38,92,0.08)" }}
    >
      <p className="mb-2 text-sm font-medium text-ink">{point.label}</p>
      {point.actualItems.length > 0 && (
        <div className="mb-2">
          <p className="mb-1 text-[0.65rem] uppercase tracking-wide text-muted-foreground">Erhalten</p>
          <ItemList items={point.actualItems} currency={currency} />
        </div>
      )}
      {point.projectedItems.length > 0 && (
        <div>
          <p className="mb-1 text-[0.65rem] uppercase tracking-wide text-muted-foreground">Erwartet</p>
          <ItemList items={point.projectedItems} currency={currency} netLabel />
        </div>
      )}
      {point.actualItems.length === 0 && point.projectedItems.length === 0 && (
        <p className="text-xs text-muted-foreground">Keine Dividenden in diesem Monat.</p>
      )}
    </div>
  );
}

/**
 * Monats-Balkenchart "bereits erhalten vs. noch erwartet" (Erik-Auftrag 2026-07-18,
 * erweitert Runde 7 Punkt 6/7): je Kalendermonat EIN gestapelter Balken - der untere,
 * volle Abschnitt ist die bereits erhaltene Netto-Dividende, der obere, schraffierte
 * Abschnitt die laut Depot-Prognose noch erwartete Netto-Dividende. Die Schraffur
 * (SVG-Pattern statt reiner Opacity-Abstufung) macht den Prognose-Charakter auch für
 * Farbfehlsichtige/beim Ausdrucken unterscheidbar, nicht nur über die Farbe.
 *
 * Runde 7 Punkt 6/7 ergänzt: Hover zeigt die einzelnen Firmen je Monat statt nur der
 * Summe (Custom-Tooltip statt Rechartsʼ Standard-Formatter, da eine Liste + Logos
 * gebraucht werden), Klick auf einen Balken filtert die "Alle Einträge"-Liste auf den
 * Monat (per AskUserQuestion geklärt: erneuter Klick hebt den Filter wieder auf), und
 * die Y-Achse nutzt jetzt computeNiceYAxisTicks() (gleiches Muster wie
 * historical-line-chart.tsx, Runde 5 Punkt 19) statt Rechartsʼ eigener Heuristik.
 */
export function ActualVsProjectedChart({
  data,
  currency,
  selectedMonth,
  onSelectMonth,
}: {
  data: MonthlyActualVsProjected[];
  currency: string;
  /** 0-11, oder undefined ohne aktiven Filter. */
  selectedMonth?: number;
  onSelectMonth?: (monthIndex: number) => void;
}) {
  const patternId = `dividendHatch-${useId()}`;
  const hasAnyValue = data.some((d) => d.actualNet > 0 || d.projectedNet > 0);

  const yAxisTicks = useMemo(
    () => computeNiceYAxisTicks(data.map((d) => d.actualNet + d.projectedNet), true),
    [data],
  );

  if (!hasAnyValue) {
    return (
      <EmptyState
        card={false}
        message="Für dieses Jahr liegen weder erhaltene noch aus dem Depot prognostizierte Dividenden vor."
      />
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {/* accessibilityLayer={false} (Feedback-Runde 13 Punkt 4, 2026-07-22): gleicher
              gelber-Fokusring-Fix wie im Fairer-Wert-Simulator, siehe dortiger Kommentar in
              fair-value-simulator.tsx fuer die volle Erklaerung. */}
          <BarChart
            data={data}
            margin={{ top: 12, right: 8, left: 4, bottom: 0 }}
            onClick={(state) => {
              // Rechartsʼ Klick-Handler liefert seit v3 nur noch activeTooltipIndex, kein
              // activePayload mehr (aeltere API) - Index in die schon bekannte `data`-Reihe
              // ist hier ohnehin robuster (kein Label-String-Abgleich noetig).
              const index = state?.activeTooltipIndex;
              if (typeof index === "number") onSelectMonth?.(data[index].monthIndex);
            }}
            accessibilityLayer={false}
          >
            {/* Ruhiger gestaltet (backlog.md Punkt 4.1, "wirken unruhig fürs Auge") - Zellgroesse
                6->10 (mehr Abstand zwischen den Linien), Strichstaerke 2.5->1.25,
                Grundflaechen-Deckkraft 0.35->0.22. Bewusst weiterhin ein Muster statt reiner
                Opacity-Abstufung (Runde 16 Punkt 4): unterscheidet Prognose von Ist-Werten auch
                fuer Farbfehlsichtige/beim Ausdrucken, nur deutlich dezenter als zuvor. */}
            <defs>
              <pattern
                id={patternId}
                width="10"
                height="10"
                patternTransform="rotate(45)"
                patternUnits="userSpaceOnUse"
              >
                <rect width="10" height="10" fill={GREEN} fillOpacity={0.22} />
                <line x1="0" y1="0" x2="0" y2="10" stroke={GREEN} strokeWidth="1.25" />
              </pattern>
            </defs>
            <CartesianGrid stroke="#16265C" strokeOpacity={0.14} strokeDasharray="3 5" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 12, fill: "#6B7280" }}
              axisLine={{ stroke: "#ECEAE3" }}
              tickLine={false}
            />
            <YAxis
              domain={[yAxisTicks[0], yAxisTicks[yAxisTicks.length - 1]]}
              ticks={yAxisTicks}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              tickFormatter={(v: number) => formatCompactCurrency(v, currency)}
              width={64}
              axisLine={false}
              tickLine={false}
            />
            {/* zIndex noetig (backlog.md -> [Punkt 12], selbe Ursache wie im
                Jahres-Pendant dividend-history-forecast-chart.tsx). */}
            <Tooltip cursor={false} wrapperStyle={{ zIndex: 50 }} content={<MonthTooltip currency={currency} />} />
            <Legend
              formatter={(value) => (value === "actualNet" ? "Erhalten (netto)" : "Erwartet (netto)")}
              wrapperStyle={{ fontSize: 12 }}
              iconType="rect"
            />
            {/* fill hier direkt (Feedback-Runde 13 Punkt 1, 2026-07-22, gleicher Fix wie in
                dividend-history-forecast-chart.tsx): ohne das faellt Rechartsʼ Auto-Legend
                mangels lesbarer Bar-Farbe auf ihr Default (schwarz) zurueck, obwohl die
                Balken selbst korrekt gruen sind. */}
            {/* Eckradius (Feedback-Runde 19 Punkt 12, 2026-07-27): auf 8px (~--radius-sm)
                angehoben, gleiche Begruendung wie in dividend-history-forecast-chart.tsx -
                barCategoryGap bewusst NICHT angefasst (anders als dort, siehe Runde 18
                Punkt 8): diese Ansicht zeigt immer exakt 12 Monatsbalken, die dortige
                "wenige Balken wirken der Luecke nach zu breit"-Ursache (variable
                Balkenanzahl je gewaehltem Zeitraum) gibt es hier strukturell nicht. */}
            <Bar
              dataKey="actualNet"
              name="actualNet"
              stackId="dividends"
              fill={GREEN}
              radius={[0, 0, 8, 8]}
              isAnimationActive={false}
              cursor="pointer"
              activeBar={{ fillOpacity: 1 }}
            >
              {data.map((d) => (
                <Cell
                  key={d.monthIndex}
                  fill={GREEN}
                  fillOpacity={selectedMonth === undefined || selectedMonth === d.monthIndex ? 1 : 0.35}
                />
              ))}
            </Bar>
            <Bar
              dataKey="projectedNet"
              name="projectedNet"
              stackId="dividends"
              fill={`url(#${patternId})`}
              stroke={GREEN}
              strokeOpacity={0.5}
              radius={[8, 8, 0, 0]}
              isAnimationActive={false}
              cursor="pointer"
              activeBar={{ strokeOpacity: 1, strokeWidth: 2 }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-muted-foreground">
        Schraffiert = noch erwartet, laut Zahlungsrhythmus deiner Depot-Positionen
        hochgerechnet, keine Garantie.
        {onSelectMonth && " Klick auf einen Balken filtert die Liste unten auf diesen Monat."}
      </p>
    </div>
  );
}
